import { ViewCreator } from '@viewCreators';
import { DashboardView } from '../views/DashboardView';
import { RoleStrings } from '../enums/Role';
import { GroupResponse } from '@models/group';
import { GroupStore, handleApiResponse, TaskStore } from '@stores';
import { DateTime } from 'luxon';
import participantView, { ParticipantView } from '@views/ParticipantView';
import { LeaderView } from '@views/LeaderView';
import { ToDateTime } from '@utils/DateUtils';
import logger from '@utils/logger';

export enum OnboardingSections {
  GoalSetting = 0,
  Pledge = 1,
  AccountabilityPartner = 2,
}

interface DashboardViewArgs {
  groupId: number;
  userId: string;
}

export class DashboardViewCreator extends ViewCreator<Promise<DashboardView>, DashboardViewArgs> {
  createView: () => Promise<DashboardView>;
  createViewWithArg: (arg: DashboardViewArgs) => Promise<DashboardView>;

  constructor(
    private readonly store: TaskStore = new TaskStore(),
    private readonly groupStore: GroupStore = new GroupStore()
  ) {
    super();

    this.createView = async (): Promise<DashboardView> => {
      throw new Error('Method not implemented');
    };

    this.createViewWithArg = async ({ groupId, userId }: DashboardViewArgs): Promise<DashboardView> => {
      let group: GroupResponse;
      try {
        group = await handleApiResponse<GroupResponse>(async () => await this.groupStore.ReadOne(groupId));
      } catch (error: any) {
        logger.error(`Error: ${error}`);
        if (error.message && error.message.includes('authorized')) {
          throw new Error('You are not authorized to view this group');
        } else {
          throw new Error('An error occurred');
        }
      }

      const paymentStartDay = 5;
      const currentDay = group.currentDay;

      // Payment: Should be required starting on day 5 (Friday of week 0, onboarding week)
      let needPayment = currentDay >= paymentStartDay && !group.pledgeReceived;

      if (
        !group
          .cohortUsers!.find((p) => p.id === group.cohortUserId)!
          .role.split('|')
          .some((x) => x === 'Participant')
      ) {
        needPayment = false;
      }

      // TODO: Remove leaderboard
      const leaderView: LeaderView = Object.keys(group.leaderboard)
        .filter((rank) => {
          return group.leaderboard[Number(rank)].participant.isActive;
        })
        .map((rank) => {
          const leader = group.leaderboard[Number(rank)];
          const cohortUser = group.cohortUsers?.find((u) => u.userId === leader.participant.userId);
          const user = cohortUser?.user;
          const participant = {
            name: user?.name,
            role: cohortUser?.role,
            avatarUrl: user?.avatarUrl,
            email: user?.email,
            id: user?.id,
            CohortUser: { id: cohortUser?.id },
          } as ParticipantView;
          return {
            [Number(rank)]: {
              participant: participant,
              successfulWeeks: leader.successfulWeeks,
              currentStreak: leader.currentStreak,
            },
          };
        })
        .reduce((view, item) => {
          const rank = Number(Object.keys(item)[0]);
          view[rank] = item[rank];
          return view;
        }, {} as LeaderView);

      return {
        groupStartDate: group.startDate,
        //TODO Remove Pot Amount
        potAmount: group.potAmount,
        pledgeAmount: group.pledgeAmount,
        individualPledgeAmount: group.individualPledgeAmount,
        externalPledgeAmount: group.externalPledgeAmount,
        //TODO: Remove Leaderboard
        leaders: leaderView,
        cohortUserId: group.cohortUserId,
        logo: group.groupLogo,
        channel: group.channel,
        groupName: group.name,
        groupId: groupId,
        tasks: [], //[...tasks] as TaskView[],
        progress: group.goalProgress,
        needPayment: needPayment,
        availableJailCards: group.availableJailCards,
        team: {
          participantCount: group.cohortUsers?.length || 0,
          participants: group.cohortUsers
            ?.filter((u) => u.userId !== Number(userId) && u.isActive)
            .map((u) => {
              return {
                ...u.user,
                rank: 0,
                isNew: false,
                score: 0,
                role: RoleStrings.Participant,
                CohortUser: u,
              } as ParticipantView;
            }) ?? [participantView.Empty],
        },
        accountabilityPartners: group.accountabilityPartners,
        currentWeekNumberIndexZero: group.currentWeekNumberIndexZero,
      };
    };
  }
}
